// /admin → Security: the devices approved to sign in as the operator, and the
// recent sign-in activity (failed attempts, blocked ones, approvals). The data
// and the rules live in api/_adminGuard.js; this only shows them.
//
// Revoking a device means its NEXT admin sign-in needs a fresh approval link. A
// session it already holds lasts until it expires (6 hours) — tokens are
// stateless, so there is nothing to reach into and cancel.
import { useCallback, useEffect, useState } from 'react';

const EVENT_LABELS = {
  login_failed: { label: 'Failed sign-in', tone: 'bad' },
  login_blocked: { label: 'Blocked (too many attempts)', tone: 'bad' },
  login_ok: { label: 'Signed in', tone: 'ok' },
  device_approval_requested: { label: 'New device asked for approval', tone: 'warn' },
  device_link_throttled: { label: 'Approval email throttled', tone: 'warn' },
  device_approved: { label: 'Device approved', tone: 'ok' },
  device_denied: { label: 'Device denied', tone: 'bad' },
  device_revoked: { label: 'Device removed', tone: 'warn' },
  // The public Eng Tour (api/_tourSecurity.js): a request the real page never
  // makes, or the completion inbox at its ceiling. `kind` says which.
  tour_suspicious: { label: 'Eng Tour: suspicious request', tone: 'warn' },
};
const TONE = {
  bad: { background: '#fce8e6', color: '#c5221f' },
  warn: { background: '#fef7e0', color: '#8a5a00' },
  ok: { background: '#e6f4ea', color: '#137333' },
};

const fmt = (ms) => {
  if (!ms) return '—';
  try { return new Date(ms).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return new Date(ms).toISOString(); }
};

export default function SecurityPanel({ apiBase, token, onUnauthorized }) {
  const [state, setState] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const call = useCallback(async (body) => {
    const r = await fetch(`${apiBase}/api/admin-device`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (r.status === 401) { onUnauthorized?.(); throw new Error('Session expired.'); }
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || 'Request failed.');
    return j;
  }, [apiBase, token, onUnauthorized]);

  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try { setState(await call({ op: 'list' })); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  }, [call]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (d) => {
    const mine = d.id === state?.current;
    const msg = mine
      ? 'Remove THIS browser? Your next sign-in here will need a new approval email.'
      : `Remove "${d.label}"? Its next admin sign-in will need a new approval email.`;
    if (!window.confirm(msg)) return;
    try { await call({ op: 'revoke', id: d.id }); await load(); }
    catch (e) { setErr(e.message); }
  };

  const devices = state?.devices || [];
  const events = state?.events || [];
  const failed24h = events.filter((e) => (e.type === 'login_failed' || e.type === 'login_blocked')
    && Date.now() - (e.at || 0) < 24 * 3600 * 1000).length;

  return (
    <div>
      {err && <div style={S.err}>{err}</div>}
      <div style={S.summary}>
        <div style={S.stat}><b>{devices.length}</b><span>approved devices</span></div>
        <div style={S.stat}><b style={failed24h ? { color: '#c5221f' } : null}>{failed24h}</b><span>failed sign-ins, last 24 h</span></div>
        <div style={S.stat}>
          <b>{state ? (state.deviceCheck ? 'On' : 'Off') : '…'}</b>
          <span>new-device approval{state && !state.deviceCheck ? ' (ADMIN_DEVICE_CHECK)' : ''}</span>
        </div>
        <button style={S.ghost} onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</button>
      </div>
      {state?.alertTo && (
        <div style={S.muted}>Failed attempts and new-device approvals are emailed to {state.alertTo}.</div>
      )}

      <div style={S.h}>Approved devices</div>
      {devices.length === 0 ? <div style={S.muted}>None yet — a device is added when you approve its sign-in link.</div> : (
        <div style={S.scroll}>
          <table style={S.table}>
            <thead><tr>{['Device', 'Approved', 'Approved from', 'Last sign-in', 'Last location', ''].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.id}>
                  <td style={S.td}>
                    {d.label || 'Unknown device'}
                    {d.id === state?.current && <span style={S.badge}>This browser</span>}
                  </td>
                  <td style={S.td}>{fmt(d.approvedAt)}</td>
                  <td style={S.td}>{d.approvedFrom || '—'}</td>
                  <td style={S.td}>{fmt(d.lastSeenAt)}</td>
                  <td style={S.td}>{[d.lastPlace, d.lastIp].filter(Boolean).join(' · ') || '—'}</td>
                  <td style={S.td}><button style={S.danger} onClick={() => revoke(d)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={S.h}>Recent sign-in activity</div>
      {events.length === 0 ? <div style={S.muted}>Nothing recorded yet.</div> : (
        <div style={S.scroll}>
          <table style={S.table}>
            <thead><tr>{['When', 'What', 'Where', 'Username', 'From', 'Device', 'Tries (1 h)'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {events.map((e, i) => {
                const meta = EVENT_LABELS[e.type] || { label: e.type, tone: 'warn' };
                return (
                  <tr key={`${e.at}-${i}`}>
                    <td style={S.td}>{fmt(e.at)}</td>
                    <td style={S.td}><span style={{ ...S.pill, ...TONE[meta.tone] }}>{meta.label}</span></td>
                    <td style={S.td}>{e.door || (e.kind ? `Eng Tour · ${e.kind}${e.tour ? ` (${e.tour})` : ''}` : '—')}</td>
                    <td style={S.td}>{e.user || '—'}</td>
                    <td style={S.td}>{[e.place, e.ip].filter(Boolean).join(' · ') || '—'}</td>
                    <td style={S.td}>{e.device || '—'}</td>
                    <td style={S.td}>{e.attemptsFromIp || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S = {
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '8px 11px', fontSize: 13, marginBottom: 12 },
  muted: { color: '#5f6368', fontSize: 13.5, margin: '4px 0 12px' },
  summary: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', margin: '4px 0 8px' },
  stat: { display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #e8eaed', borderRadius: 12, padding: '10px 14px', minWidth: 140, fontSize: 12.5, color: '#5f6368' },
  h: { fontSize: 12, fontWeight: 800, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '20px 0 8px' },
  scroll: { overflowX: 'auto', background: '#fff', border: '1px solid #e8eaed', borderRadius: 12 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e8eaed', color: '#5f6368', fontWeight: 700, whiteSpace: 'nowrap' },
  td: { padding: '9px 12px', borderBottom: '1px solid #f1f3f4', verticalAlign: 'top' },
  badge: { marginLeft: 8, fontSize: 11, fontWeight: 800, background: '#e8f0fe', color: '#1a73e8', borderRadius: 999, padding: '2px 8px' },
  pill: { fontSize: 11.5, fontWeight: 800, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' },
  ghost: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginLeft: 'auto' },
  danger: { background: '#fff', color: '#c5221f', border: '1.5px solid #f4c7c3', borderRadius: 8, padding: '5px 10px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
};
